import * as v from "valibot";
import { formatDateISO, parseCoordinate } from "./formats.common";
export * as v from "valibot";

export function parseable<T extends v.BaseSchema<unknown, unknown, any>>(schema: T) {
    return (data: unknown) =>
        v.safeParse(schema, data, {
            abortEarly: true,
            abortPipeEarly: true,
        });
}

export function parseableAsync<T extends v.BaseSchema<unknown, unknown, any>>(schema: T) {
    return async (data: unknown) =>
        await v.safeParseAsync(schema, data, {
            abortEarly: true,
            abortPipeEarly: true,
        });
}

export const requireds = {
    typed: <T>() =>
        v.pipe(
            v.nonNullish(v.unknown(), "Wajib diisi."),
            v.transform((input) => input as NonNullable<T>),
        ),
    string: v.pipe(v.string("Wajib diisi."), v.trim(), v.minLength(1, "Wajib diisi.")),
    number: v.number("Wajib diisi."),
    boolean: v.boolean("Wajib diisi."),
    date: v.pipe(
        v.date("Wajib diisi."),
        v.transform((input) => formatDateISO(input)),
    ),
    email: v.pipe(v.string("Wajib diisi."), v.trim(), v.email("Format email salah.")),
    phone: v.pipe(
        v.string("Wajib diisi."),
        v.trim(),
        v.transform((p) => p.replace(/^[^+0-9]+/g, "").replace(/[^0-9]+$/g, "")),
        v.regex(/\+62\d+/, "Format no. telepon salah. Gunakan kode area +62 di awal."),
        v.regex(/\+62\d{9,13}/, "Format no. telepon salah. Panjang nomor tidak sesuai."),
        v.regex(/^\+62\d{9,13}$/, "Format no. telepon tidak sesuai."),
    ),
    priority: v.pipe(
        v.number("Wajib diisi."),
        v.integer("Wajib diisi."),
        v.minValue(1, "Wajib diisi."),
        v.maxValue(10, "Wajib diisi."),
    ),
    coordinate: v.pipe(
        v.string("Wajib diisi."),
        v.regex(/\d+, \d+/, "Format koordinat salah."),
        v.transform((input) => parseCoordinate(input)),
    ),
    identityNumber: v.pipe(
        v.string("Wajib diisi."),
        v.trim(),
        v.regex(/^\d+$/, `Format salah, terdapat karakter bukan angka.`),
        v.regex(/^\d{16}$/, `Format salah, panjang tidak sesuai.`),
    ),
    typedArray: <T>() =>
        v.pipe(
            v.array(v.nonNullish(v.unknown(), "Wajib diisi.")),
            v.minLength(1, "Wajib diisi."),
            v.transform((input) => input as NonNullable<T[]>),
        ),
    stringArray: v.pipe(v.array(v.string(), "Wajib diisi."), v.minLength(1, "Wajib diisi.")),
    options: <T extends string | number>(options: T[]) => v.picklist(options, "Wajib diisi."),
    minNumber: (min: number, customMessage?: string) =>
        v.pipe(
            v.number("Wajib diisi."),
            v.minValue(min, customMessage ?? `Nilai tidak boleh kurang dari ${min}.`),
        ),
    ipv4: v.pipe(v.string("Wajib diisi."), v.trim(), v.ipv4("Format alamat IPv4 salah.")),
    port: v.pipe(
        v.number("Wajib diisi."),
        v.integer("Port harus berupa bilangan bulat."),
        v.minValue(1, "Port tidak boleh kurang dari 1."),
        v.maxValue(65535, "Port tidak boleh lebih dari 65535."),
    ),
    cidr: v.pipe(
        v.number("Wajib diisi."),
        v.integer("CIDR harus berupa bilangan bulat."),
        v.minValue(16, "CIDR tidak boleh kurang dari 16."),
        v.maxValue(32, "CIDR tidak boleh lebih dari 32."),
    ),
    file: v.pipe(
        v.string("Wajib diisi."),
        v.check((input) => input.startsWith("data:"), "Wajib diisi"),
    ),
    fileImage: v.pipe(
        v.string("Wajib diisi."),
        v.check((input) => input.startsWith("data:image/"), "Wajib diisi"),
    ),
    objectWithId: v.object({ id: v.string() }, "Wajib diisi."),
};

export const optionals = {
    email: v.optional(requireds.email),
    priority: v.optional(requireds.priority),
    coordinate: v.optional(requireds.coordinate),
    identityNumber: v.optional(requireds.identityNumber),
    stringArray: v.optional(v.pipe(v.array(v.string()))),
    fileImage: v.optional(requireds.fileImage),
    port: v.optional(requireds.port),
    minNumber: (min: number, customMessage?: string) =>
        v.optional(requireds.minNumber(min, customMessage)),
};
